import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { ClusterAnalysis } from "../api/patients";

/**
 * Транскрипция с редактированием + кластерные чипсы (для animals).
 * Универсальный компонент: сохранение параметризовано колбэком, поэтому
 * работает и для записей кабинета (PATCH neurologist-audio), и для
 * аудио-блоков единого теста (PATCH /v3/scan-audio/{id}).
 */
export default function TranscriptionBlock({
  transcription,
  clusterAnalysis,
  emptyHint = "Речь не распознана — можно внести текст вручную",
  onSave,
}: {
  transcription: string | null;
  clusterAnalysis?: ClusterAnalysis | null;
  emptyHint?: string;
  onSave: (text: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(transcription || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setText(transcription || "");
  }, [transcription]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    setError("");
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
          Распознанный текст:
        </Typography>
        {!editing && (
          <Button size="small" sx={{ minWidth: 0, p: 0.3 }} onClick={() => setEditing(true)}>
            <EditIcon sx={{ fontSize: 14 }} />
          </Button>
        )}
      </Box>

      {editing ? (
        <Box sx={{ mt: 0.25 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            size="small"
            sx={{ bgcolor: "white" }}
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 14 }} />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Пересчёт..." : "Сохранить"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setEditing(false);
                setText(transcription || "");
              }}
              disabled={saving}
            >
              Отмена
            </Button>
          </Box>
        </Box>
      ) : transcription ? (
        <Typography
          variant="body2"
          sx={{ mt: 0.25, bgcolor: "white", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
        >
          {transcription}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontStyle: "italic" }}>
          {emptyHint}
        </Typography>
      )}

      {clusterAnalysis && (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 0.5, flexWrap: "wrap" }}>
            <Chip label={`${clusterAnalysis.total_unique} уник. животных`} size="small" color="primary" variant="outlined" />
            <Chip label={`${clusterAnalysis.cluster_count} кластеров`} size="small" color="secondary" variant="outlined" />
            <Chip label={`${clusterAnalysis.switches} переключений`} size="small" variant="outlined" />
            {clusterAnalysis.repetitions.length > 0 && (
              <Chip
                label={`Повторы: ${clusterAnalysis.repetitions.join(", ")}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          {clusterAnalysis.clusters.map((cl, ci) => (
            <Box key={ci} sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {cl.name}:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {cl.animals.join(", ")}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
